import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { UserAuthService } from '../UserAuth.service';
import { AdminCacheService } from '../admin/services/admin-cache.service';
import { ActorDisplay } from '../shared/actor-display';

/**
 * Phase 3: invitations, binding links and customer sign-in models.
 *
 * CONTRACT: DataServices redhaw/phase3-customer-model-invites @ bce4228, extended by
 * redhaw/admin-console-v2 (tenant-user-management, 2026-10-02: domains, create, configure, phone) (relayed
 * by tenant-user-management, 2026-10-01). Every endpoint is a JSON POST under the
 * DataServices base URL, and every endpoint 404s until InvitationsEnabled=true.
 * Admin routes live under management/, NOT admin/: the Functions host reserves
 * /admin, so an admin/ route would 404 forever (contract change, bce4228).
 * Errors are {error, message}; `message` is written for a human and is shown
 * AS-IS, never paraphrased.
 */

export type IdentityModel = 'byot-entra' | 'byot-google' | 'external';
/** links/info still reports a kind; only 'invite' is honoured since binding links were retired. */
export type LinkKind = 'invite' | 'binding';
export type LinkStatus = 'pending' | 'accepted' | 'revoked' | 'expired';
/** The only roles an invite may grant. SuperAdmin is NEVER invitable. */
export type InvitableRole = 'administrator' | 'customer';
export const INVITABLE_ROLES: readonly InvitableRole[] = ['administrator', 'customer'];

export interface LinkInfo {
  kind: LinkKind;
  model: IdentityModel;
  status: LinkStatus | string;
  expiresAt?: string;
}

export interface InviteAccepted {
  accepted: boolean;
  role: string;
}

export interface InviteCreated {
  inviteId: string;
  expiresAt: string;
  delivery: string;
  link?: string;
}

export interface InviteRow {
  inviteId: string;
  email: string;
  role: string;
  model: IdentityModel | string;
  status: string;
  createdAt: string;
  expiresAt: string;
  lastSentAt?: string;
  sendCount?: number;
  acceptedAt?: string;
}

/** resolve-domain: what a domain binds to, shown to the admin BEFORE anything is saved. */
export interface ResolvedDomain {
  /** Normalised (lowercase). */
  domain: string;
  /** Entra only: the tenant the domain resolves to. */
  tid?: string;
  /** "<tid>" (Entra) or "google:<domain>". */
  directory: string;
  /** True for the Vortex staff directory: needs the explicit override. */
  staffDirectory: boolean;
}

/** How a customer domain got onto the list (DS 056ad3a). Absent on an older server: treat as manual. */
export type DomainSource = 'manual' | 'derived';

export interface CustomerDomain {
  domain: string;
  addedAt?: string;
  addedBy?: string;
  /** Who added it; a derived domain's name is "Vericure (automatic)". */
  addedByUser?: ActorDisplay | null;
  source?: DomainSource;
  /** Derived only: the sign-in that first proved it. */
  derivedAt?: string;
  /** Derived only: the latest confirming sign-in. */
  lastActiveAt?: string;
  /** Derived only: no confirming sign-in for 90 days. Never auto-removed; staff decide. */
  stale?: boolean;
}

/** A customer Administrator's read-only view of their own domains: no actors, tid or activity. */
export interface MyCustomerDomains {
  customerId: string;
  domains: { domain: string; source?: DomainSource; addedAt?: string }[];
}

export interface CustomerDomains {
  /**
   * The EFFECTIVE model (CustomerOnboardingService.ListDomainsAsync: check.Effective):
   * stored, or derived from an existing binding. null only for a customer with
   * neither, which is the one that needs "Configure sign-in".
   */
  model: IdentityModel | null;
  tid?: string;
  domains: CustomerDomain[];
}

export interface CustomerSetup {
  model: IdentityModel;
  domains: string[];
  /** Entra: the tenant the admin confirmed; every domain must resolve to it. */
  confirmedTid?: string;
  /** Binding the Vortex staff directory needs this, explicitly (Joel's option A). */
  allowStaffDirectory?: boolean;
}

export interface CustomerCreated {
  customerId: number | string;
  name: string;
  model: IdentityModel;
  domains: CustomerDomain[];
  tid?: string;
}

export interface IdentityModelState {
  effective: IdentityModel | null;
  ok: boolean;
  reason?: string;
}

/** A refused call: HTTP status, the stable machine code, and the human message. */
export class LinksApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string | null,
    readonly serverMessage: string | null,
    readonly original?: unknown
  ) {
    super(serverMessage ?? code ?? `HTTP ${status}`);
    this.name = 'LinksApiError';
  }
}

/** {error, message} out of an error body, parsed or as text. */
export function toLinksApiError(err: unknown): LinksApiError {
  const http = err as HttpErrorResponse;
  let body: unknown = http?.error;
  if (typeof body === 'string') {
    try {
      body = JSON.parse(body);
    } catch {
      body = null;
    }
  }
  const b = (body ?? {}) as { error?: unknown; message?: unknown };
  return new LinksApiError(
    typeof http?.status === 'number' ? http.status : 0,
    typeof b.error === 'string' ? b.error : null,
    typeof b.message === 'string' ? b.message : null,
    err
  );
}

/**
 * Every DataServices body declares CustomerId as an INT (InvitationsApi.cs). A
 * string "7" only worked because ReadBodyAsync uses Newtonsoft, which coerces it.
 * Send the number the contract declares rather than lean on that.
 */
export function withNumericCustomerId(body: unknown): unknown {
  if (!body || typeof body !== 'object') return body;
  const b = body as Record<string, unknown>;
  const id = b['customerId'];
  return typeof id === 'string' && /^\d+$/.test(id) ? { ...b, customerId: Number(id) } : body;
}

@Injectable({ providedIn: 'root' })
export class LinksApiService {
  constructor(
    private http: HttpClient,
    private userAuth: UserAuthService,
    private adminCache: AdminCacheService
  ) {}

  /**
   * A write here that changes CUSTOMERS must clear the admin cache, exactly as
   * AdminApiService's own writes do (its `invalidating`). Without it a customer
   * created through Add Customer stayed missing from the Customers list and every
   * customer picker until a full page reload (Joel, hybrid-dev, 2026-10-02): the
   * list is served from AdminCacheService, and only AdminApiService cleared it.
   * There is no server-side cache on GetAdminCustomers (DataServices main 8d81610);
   * this client cache is the only one.
   */
  private invalidatingCustomers<T>(write: Observable<T>, structural: boolean): Observable<T> {
    return write.pipe(tap(() => this.adminCache.invalidate('customers', structural)));
  }

  // ---- public landing pages (no session needed for info) ----

  linkInfo(customerId: string, linkId: string, secret: string): Observable<LinkInfo> {
    return this.post<LinkInfo>('links/info', { customerId, linkId, secret }, false);
  }

  acceptInvite(customerId: string, inviteId: string, secret: string): Observable<InviteAccepted> {
    return this.post<InviteAccepted>('invites/accept', { customerId, inviteId, secret }, false);
  }

  // ---- admin console: invitations ----

  createInvite(
    customerId: string,
    email: string,
    role: InvitableRole,
    displayName?: string,
    phone?: string
  ): Observable<InviteCreated> {
    if (!INVITABLE_ROLES.includes(role)) {
      // Defence in depth: the server refuses it too, but the client never asks.
      return throwError(() => new LinksApiError(0, 'role-not-invitable', `The role '${role}' cannot be invited.`));
    }
    const body: Record<string, string> = { customerId, email, role };
    if (displayName && displayName.trim()) body['displayName'] = displayName.trim();
    // E.164, optional, for future SMS alerts; carried onto the user at accept.
    if (phone) body['phone'] = phone;
    return this.post<InviteCreated>('management/invites', body);
  }

  listInvites(customerId: string): Observable<InviteRow[]> {
    return this.post<InviteRow[]>('management/invites/list', { customerId });
  }

  resendInvite(customerId: string, inviteId: string): Observable<unknown> {
    return this.post('management/invites/resend', { customerId, inviteId });
  }

  revokeInvite(customerId: string, inviteId: string): Observable<unknown> {
    return this.post('management/invites/revoke', { customerId, inviteId });
  }

  // ---- SuperAdmin customer panel ----

  resolveDomain(model: IdentityModel, domain: string, customerId?: string): Observable<ResolvedDomain> {
    const body: Record<string, string> = { model, domain };
    if (customerId) body['customerId'] = customerId;
    return this.post<ResolvedDomain>('management/customers/resolve-domain', body);
  }

  /** ONE atomic call: the customer, its model, its domains and their bindings. */
  createCustomer(name: string, setup: CustomerSetup): Observable<CustomerCreated> {
    return this.invalidatingCustomers(
      this.post<CustomerCreated>('management/customers/create', { name, ...setup }),
      true
    );
  }

  /** A legacy customer with no stored model: the same rules as create (409 already-configured otherwise). */
  configureCustomer(customerId: string, setup: CustomerSetup): Observable<unknown> {
    return this.invalidatingCustomers(this.post('management/customers/configure', { customerId, ...setup }), false);
  }

  listDomains(customerId: string): Observable<CustomerDomains> {
    return this.post<CustomerDomains>('management/customers/domains/list', { customerId });
  }

  addDomain(
    customerId: string,
    domain: string,
    confirmedTid?: string,
    allowStaffDirectory?: boolean
  ): Observable<unknown> {
    const body: Record<string, unknown> = { customerId, domain };
    if (confirmedTid) body['confirmedTid'] = confirmedTid;
    if (allowStaffDirectory) body['allowStaffDirectory'] = true;
    return this.invalidatingCustomers(this.post('management/customers/domains/add', body), false);
  }

  /** GET management/customers/domains/mine: the caller's own customer (an Administrator's view). */
  myDomains(): Observable<MyCustomerDomains> {
    return this.http
      .get<MyCustomerDomains>(`${environment.apiUrl}management/customers/domains/mine`, { params: this.identityParams() })
      .pipe(catchError((err) => throwError(() => toLinksApiError(err))));
  }

  removeDomain(customerId: string, domain: string): Observable<unknown> {
    return this.invalidatingCustomers(this.post('management/customers/domains/remove', { customerId, domain }), false);
  }

  getIdentityModel(customerId: string): Observable<IdentityModelState> {
    return this.post<IdentityModelState>('management/customers/identity-model/get', { customerId });
  }

  /**
   * One POST, one error shape. Admin calls carry the same identity query the
   * other Admin BFF calls send (admin-api.service.ts params()); the bearer comes
   * from the interceptor. The server authorizes; nothing here is trusted.
   */
  private post<T>(endpoint: string, body: unknown, admin = true): Observable<T> {
    const options = admin ? { params: this.identityParams() } : {};
    return this.http
      .post<T>(`${environment.apiUrl}${endpoint}`, withNumericCustomerId(body), options)
      .pipe(catchError((err) => throwError(() => toLinksApiError(err))));
  }

  private identityParams(): HttpParams {
    return new HttpParams()
      .set('tenantId', this.userAuth.getCurrentCustomerTenantId())
      .set('objectId', this.userAuth.getObjectId())
      .set('customerId', this.userAuth.getActiveCustomerId());
  }
}
